import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import { MediaRegistry } from '../registries/MediaRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { setFullMediaTranslation } from 'js/src/agent/video-state';
import { isNonRecommendable } from 'js/src/agent/video-state/serialization/assetSerializer';
import {
    describeAspectRatio,
    getMediaFrameFit,
    getMediaUuidFromAsset,
} from 'js/src/agent/video-state/serialization/mediaHelpers';
import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { setBrightnessUserOverride, setCropUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    applyUserMediaKenburnOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
} from 'js/src/luminary/luminary-operations';

export const setMediaSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. media_1)'),
    media_id: z
        .string()
        .optional()
        .describe('The ID of the media to apply to the asset. Must be an ID from search results.'),
    volume: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe('Volume level for the media asset (0.0 to 1.0)'),
    brightness: z
        .number()
        .min(0)
        .max(1.5)
        .optional()
        .describe(
            'Brightness level for the media asset (0=completely black, 0.5=dimmed, 1=default, 1.2=bright, 1.5=very bright)',
        ),
    position: z
        .object({
            // x/y stay required (inner required: ['x','y']) AND carry default 0, so use .meta
            // rather than .default (which would make them optional and drop them from required).
            x: z.number().meta({ description: 'X value in pixels.', default: 0 }),
            y: z.number().meta({ description: 'Y value in pixels.', default: 0 }),
        })
        .optional()
        .describe(
            "Translate the media relative to the center of the asset's bounds. The translation range is determined by the asset's mediaXTranslationRange and mediaYTranslationRange property. If the range is too narrow, increase the media's scale.",
        ),
    scale: z
        .number()
        .min(10)
        .max(1000)
        .default(100)
        .describe('Scale value for the media in percentage.'),
    kenburn: z.enum(VideoData.ALL_BACKGROUND_ANIMATIONS).optional().describe('Ken Burns effect'),
});
export type SetMediaArgs = z.infer<typeof setMediaSchema>;

export interface SetMediaToolConstructorArgs extends ToolConstructorArgs {
    /**
     * When true (default), changing a scene's media fetches and applies recommended
     * scene attributes (crop, brightness, Ken Burns, etc.) inline, matching a manual
     * media swap. Set to false for bulk flows like the Media Optimizer, which apply
     * recommendations in a single batch at the end of the run to avoid spawning a
     * WebGL context per scene (which can cause context-loss errors).
     */
    recommendSceneAttributes?: boolean;
}

export class SetMediaTool extends VideoStateTool<
    typeof setMediaSchema,
    SetMediaToolConstructorArgs
> {
    protected override readonly schema = setMediaSchema;
    protected override readonly toolName = TOOL_NAMES.setMedia;
    protected override readonly toolDescription = `Set media, position, scale or volume for a specific scene and asset.

Position and scale reframe the media inside a fixed frame, and the position axes are inverted — load the \`${SKILL_NAMES.cropMedia}\` skill before using them.`;

    public override customRenderer = SetMediaResultRenderer;

    private readonly recommendSceneAttributes: boolean;

    public constructor(args: SetMediaToolConstructorArgs) {
        super(args);
        this.recommendSceneAttributes = args.recommendSceneAttributes ?? true;
    }

    public override getToolDisplayName(): string {
        return 'Setting media';
    }

    // The resolved global index of the scene edited by the last successful execute(), used by
    // onEditApplied — the raw args may address the scene by scene_id rather than scene_index.
    private lastEditedSceneIndex: number | undefined;

    protected override onEditApplied(): void {
        // Changing media on the first scene changes the thumbnail.
        if (this.lastEditedSceneIndex === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'image';
    }

    protected override preprocessArgs(args: SetMediaArgs): SetMediaArgs {
        // position.x/y are required-with-default(0) inside the optional position object.
        // .meta({ default }) emits the default but does not fill it at parse time (unlike the
        // legacy Ajv useDefaults path), so a partial position like { x: 5 } would hard-fail on the
        // missing y. Fill the missing axis with 0 here (before validation) to preserve the old
        // behavior — the emitted schema is unchanged.
        if (args.position && typeof args.position === 'object' && !Array.isArray(args.position)) {
            // The arg type says position is complete, but at parse time it may be partial, so cast
            // to optional axes to make the 0-defaults meaningful.
            const position = args.position as { x?: number; y?: number };
            args = { ...args, position: { x: 0, y: 0, ...position } };
        }
        return args;
    }

    public async execute(args: SetMediaArgs, context: ToolExecutionContext) {
        const { asset_name, media_id, volume, brightness, position, scale, kenburn } = args;
        const { parentActionGroup } = context;

        const changes: string[] = [];
        let mediaChanged = false;
        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            this.lastEditedSceneIndex = scene_index;

            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId} which uses design ${scene.sceneDesign}`;
            }
            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            if (isNonRecommendable(scene, asset_name, this.getState())) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            if (!VideoData.isMediaAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a media asset`;
            }

            // Resolve (but don't yet apply) the new media so we can validate it up
            // front and apply the swap LAST — after the explicit Ken Burns /
            // brightness overrides below are registered. Swapping media triggers a
            // scene-attribute recommendation that only leaves an attribute alone if
            // the user has overridden it, so the overrides must exist first.
            let mediaSourceData: ReturnType<MediaRegistry['getMediaSourceData']> = null;
            if (media_id !== undefined) {
                const mediaRegistry = MediaRegistry.getInstance();
                if (!mediaRegistry.hasItem(media_id)) {
                    return `Error: Unknown media ${media_id}`;
                }
                mediaSourceData = mediaRegistry.getMediaSourceData(media_id);
                if (!mediaSourceData) {
                    return `Error: Unknown media ${media_id}`;
                }
            }
            if (volume !== undefined && !asset.isVideo) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} contains an image and does thus not support volume control`;
            }

            const { applyUserMediaKenburn, fetchAndSetRecommendedSceneAttributes } =
                makeOperationsCallable(
                    {
                        applyUserMediaKenburnOperation,
                        fetchAndSetRecommendedSceneAttributesOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    this._getState,
                );

            // Explicit Ken Burns wins: set the value AND register the user override
            // so the media-swap recommendation below (and any future swap or
            // scene-design change) leaves it alone. All dispatches share
            // parentActionGroup, so they collapse into a single undo checkpoint.
            if (kenburn !== undefined) {
                applyUserMediaKenburn(scene.id, asset.id, kenburn, false, parentActionGroup);
                changes.push(`media animation to ${kenburn}`);
            }

            // Explicit brightness wins the same way. Register the override
            // synchronously here — the dedicated brightness operation debounces its
            // override dispatch, which would land after the recommendation has
            // already read the override state.
            if (brightness !== undefined) {
                asset.brightness = brightness;
                this.dispatch(
                    setBrightnessUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                changes.push(`media brightness to ${brightness}`);
            }

            if (volume !== undefined) {
                asset.volume = volume;
                changes.push(`media volume to ${volume}`);
            }

            // Swap media before applying scale/position. Swapping sourceData resets
            // crop/scale/flip transformations (but NOT kenburn, so the value set
            // above survives), so any explicit scale/position must be applied AFTER
            // the swap or they would be silently wiped. The scene-attribute
            // recommendation below runs with autoScale: false, so it won't restore
            // them either.
            if (mediaSourceData) {
                try {
                    asset.sourceData = mediaSourceData;
                } catch (error) {
                    return `Error: data wrong format for asset ${asset_name} in scene ${sceneShortId}.`;
                }
                changes.push(`set media for ${asset_name} to ${media_id}`);
                mediaChanged = true;
            }

            // Apply scale before position, as scale affects the translation range.
            if (scale) {
                const scaleValue = scale / 100;
                asset.scaleX = scaleValue;
                asset.scaleY = scaleValue;
                changes.push('media scale');
            }
            if (position) {
                setFullMediaTranslation(asset, position.x, position.y);

                const xDirection = position.x === 0 ? 'center' : position.x < 0 ? 'right' : 'left';
                const yDirection = position.y === 0 ? 'center' : position.y < 0 ? 'down' : 'up';
                changes.push(
                    `media position to ${position.x} (${xDirection}), ${position.y} (${yDirection})`,
                );
            }

            // Scale and position are crop transformations. Explicit values win:
            // register the crop user override so the media-swap recommendation
            // below leaves them alone — it otherwise resets crop (including scale,
            // via setMediaCrop's default) unless the user has overridden it.
            if (scale || position) {
                this.dispatch(
                    setCropUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            if (mediaChanged && this.recommendSceneAttributes) {
                // Fetch and apply recommended scene attributes (crop, brightness,
                // Ken Burns, text position, watermark contrast) for the changed
                // media, matching what happens on a manual media swap. Attributes
                // the user set explicitly above are marked as overridden, so the
                // recommendation leaves those alone. This operation checkpoints the
                // video itself.
                await fetchAndSetRecommendedSceneAttributes({
                    sceneId: scene.id,
                    autoCrop: true,
                    recommendBrightness: true,
                    systemTriggered: true,
                    parentActionGroup,
                    autoScale: false,
                });
            } else if (changes.length > 0) {
                // Only non-media properties changed, or recommendations are applied
                // in batch by the caller (e.g. the Media Optimizer). Checkpoint the
                // final state here; sharing parentActionGroup groups it with any
                // override dispatches above into a single undo step.
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            this.recordSceneFill(scene_index, 'media');

            return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${sceneShortId}${this.buildReuseWarning(scene_index, mediaChanged ? media_id : undefined, mediaSourceData?.uuid)}${mediaChanged ? buildAspectRatioNote(asset, asset_name) : ''}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting media: ${errorMessage}`;
        }
    }

    /**
     * Warns when the media just applied also appears on other scenes — repeated
     * media reads as monotonous and causes ugly animation restarts, so reuse
     * should be a deliberate choice, not an accident.
     *
     * Matching is by source uuid rather than registry id: `getMediaIdFromAsset`
     * needs the MediaRegistry to reverse-map the uuid, and mid-build the registry
     * often hasn't indexed the media on the scenes already carrying it — those
     * assets resolve to "unregistered" and the reuse went unreported. The uuid is
     * on the asset itself, so it is available whatever the registry knows.
     */
    private buildReuseWarning(
        sceneIndex: number,
        mediaId: string | undefined,
        mediaUuid: string | undefined,
    ): string {
        if (!mediaId || !mediaUuid) {
            return '';
        }
        try {
            const otherSceneIndexes: number[] = [];
            this.luminary.scenes.forEach((scene, index) => {
                if (index === sceneIndex) {
                    return;
                }
                const hasSameMedia = scene
                    .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
                    .some((asset) => getMediaUuidFromAsset(asset) === mediaUuid);
                if (hasSameMedia) {
                    otherSceneIndexes.push(index);
                }
            });
            if (otherSceneIndexes.length === 0) {
                return '';
            }
            const sceneList = otherSceneIndexes.join(', ');
            return ` WARNING: media ${mediaId} already appears on scene${otherSceneIndexes.length > 1 ? 's' : ''} ${sceneList}. Repeating media makes the video monotonous and causes ugly animation restarts — use a different clip unless the repetition is deliberate.`;
        } catch {
            // The warning is best-effort advice; never fail the set over it.
            return '';
        }
    }
}

// Roughly 36% of the media left on screen. Below this the crop is ordinary framing — a half-canvas
// panel holding a 16:9 clip sits at 2x and is usually deliberate — so staying quiet keeps the note
// meaningful when it does appear. Starting point; worth revisiting once we see how it reads.
const ASPECT_RATIO_MISMATCH_THRESHOLD = 2.75;

/**
 * Deliberately a note rather than an error: a tight crop is sometimes exactly what the design
 * wants, and the agent is the one positioned to tell an intentional close-up from an accident.
 */
const buildAspectRatioNote = (asset: TimeAwareMediaAsset, assetName: string): string => {
    try {
        const fit = getMediaFrameFit(asset);
        if (!fit || fit.mismatch < ASPECT_RATIO_MISMATCH_THRESHOLD) {
            return '';
        }

        const visiblePercent = Math.round(fit.visibleFraction * 100);
        const mediaShape = describeAspectRatio(fit.mediaAspect);
        const frameShape = describeAspectRatio(fit.frameAspect);

        return ` ASPECT RATIO: the media is ${mediaShape} (${fit.mediaAspect.toFixed(2)}:1) but ${assetName} is ${frameShape} (${fit.frameAspect.toFixed(2)}:1), so only about ${visiblePercent}% of the media is visible — it is cropped to fill the frame and the subject may be cut off. If that crop is deliberate, keep it. Otherwise pick media shaped like the frame, use a scene design that fits this media, or set position/scale to bring the subject back into view.`;
    } catch {
        // The note is best-effort advice; never fail the set over it.
        return '';
    }
};

const SetMediaResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const mediaId = args.media_id;
    const mediaRegistry = MediaRegistry.getInstance();
    const mediaData = mediaId ? mediaRegistry.getItem(mediaId) : null;
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
            {mediaData && (
                <div>
                    {React.createElement(BackgroundAsset, {
                        searchContext: null,
                        media: ImmutableFromJS(mediaData),
                        media_id: 0,
                        result_index: 0,
                    })}
                </div>
            )}
        </div>
    );
};
